/**
 * Centralized fetch wrapper with global 401 handling.
 *
 * On any 401 response from an API call:
 *   1. Clear auth tokens from localStorage
 *   2. Redirect to /login?reason=expired
 *   3. Reject the promise so calling code can short-circuit cleanly
 *
 * Skipped when the user is already on /login (avoids redirect loops during
 * the login flow itself, where 401 from /api/auth/login is expected and means
 * "bad credentials", not "session expired").
 *
 * To adopt: in any service, replace `fetch(...)` with `apiFetch(...)`.
 * Same signature, same return type — no other changes needed.
 */

const TOKEN_KEYS = ['token', 'refreshToken', 'user', 'persist:root'];

/** Where the sign-in screen actually lives. */
const LOGIN_PATH = '/login';

/**
 * redux-persist rewrites in-memory state on a throttle, so clearing storage alone
 * lets the token reappear on the next load and bounce the user back to the screen
 * that failed. Registered from index.tsx rather than imported, to avoid the cycle
 * store -> slices -> services -> this module.
 */
let persistor: { pause: () => void; purge: () => Promise<any> } | null = null;

export const registerPersistor = (instance: typeof persistor): void => {
  persistor = instance;
};

/** True once a redirect is under way, so several failing calls trigger one. */
let redirecting = false;

const endExpiredSession = () => {
  if (redirecting) return;
  redirecting = true;

  const leave = () => {
    TOKEN_KEYS.forEach((key) => localStorage.removeItem(key));
    // replace() so the expired URL does not sit in history.
    window.location.replace(`${LOGIN_PATH}?reason=expired`);
  };

  if (persistor) {
    persistor.pause();
    persistor.purge().then(leave, leave);
  } else {
    leave();
  }
};

export async function apiFetch(input: RequestInfo, init?: RequestInit): Promise<Response> {
  const response = await fetch(input, init);

  // Already on the sign-in screen: a 401 there means bad credentials, not an
  // expired session, and redirecting would loop.
  if (response.status === 401 && window.location.pathname !== LOGIN_PATH) {
    endExpiredSession();
    throw new Error('Session expired');
  }

  return response;
}
